// Gestão de Frota (módulo "frota", pedido 2026-10-01) — painel com
// gráficos, veículos, motoristas, custos e manutenção, tudo real
// (backend src/fleet-management + relatórios do rastreamento). Quem vê:
// admin sempre; funcionário/cliente frotista com a permissão "Gestão de
// Frota". Cliente só enxerga a própria frota (o backend força isso); a
// equipe escolhe de qual cliente quer ver no seletor do topo.
import { createFileRoute, Link } from "@tanstack/react-router";
import { GpsInlineLoader } from "@/components/gps-loader";
import { useMemo, useState, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  CalendarClock,
  ArrowDown,
  ArrowUp,
  Car,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  ClipboardCheck,
  Clock,
  DollarSign,
  FileBarChart,
  Bell,
  MessageCircle,
  Gauge,
  KeyRound,
  Loader2,
  ParkingCircle,
  Pencil,
  Plus,
  Route as RouteIcon,
  Search,
  Settings,
  Trash2,
  UserRound,
  Wrench,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/list-pagination";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useVehicles } from "@/hooks/queries";
import { DriverCard, DriverProfileDialog } from "@/components/fleet-driver-profile";
import { FleetVehicleReport } from "@/components/fleet-vehicle-report";
import { InspectionsBrowser } from "@/components/vehicle-inspections-browser";
import { InspectionChecklistEditor } from "@/components/vehicle-inspection-checklist-editor";
import { useVehicleInspectionAccess } from "@/hooks/queries/vehicle-inspections";
import { InspectionLinkDialog, inspectionLinkSkipConfirm, sendInspectionLinkDirect } from "@/components/inspection-link-dialog";
import { InspectionLinksList } from "@/components/inspection-links-list";
import {
  useFleetAccess,
  useFleetContractTemplates,
  useFleetList,
  useFleetMutations,
  useFleetOwners,
  useFleetReports,
  useFleetSummary,
  useFleetServerStats,
  useFleetSettings,
  useFleetVehicleIds,
  useFleetMaintenanceServices,
  useSaveFleetMaintenanceServices,
} from "@/hooks/queries/fleet-management";
import {
  FLEET_EXPENSE_CATEGORIES,
  fleetManagementService,
  type FleetContractTemplate,
  type FleetAccess,
  type FleetDriver,
  type FleetExpense,
  type FleetMaintenance,
  type FleetMaintenanceService,
  type FleetSummary,
} from "@/services/gpsdrive-fleet-management.service";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import type { Vehicle } from "@/types";
import { NAME_COLLATOR, compareVehiclesByStatus } from "@/lib/vehicle-status-sort";
import { LicensePlate } from "@/components/license-plate";

export const Route = createFileRoute("/frota")({
  head: () => ({ meta: [{ title: "Gestão de Frota" }] }),
  component: FleetPageGate,
});

// ---------------------------------------------------------------------------
// Utilitários

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const km = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
const money = (v: number) => brl.format(v || 0);

function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function brDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}
function hours(ms: number): string {
  const h = ms / 3_600_000;
  if (h >= 100) return `${km.format(h)} h`;
  const whole = Math.floor(h);
  const min = Math.round((h - whole) * 60);
  return whole ? `${whole}h${String(min).padStart(2, "0")}` : `${min} min`;
}
function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const today = new Date(isoLocal(new Date()) + "T00:00:00");
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}
function errMsg(err: unknown): string {
  return err instanceof GpsDriveApiError || err instanceof Error ? err.message : "Tente novamente.";
}
const toNum = (v: string) => (v.trim() === "" ? null : Number(v.replace(",", ".")));

type PresetKey = "7d" | "30d" | "mes" | "mes_passado";
const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "7d", label: "7 dias" },
  { key: "30d", label: "30 dias" },
  { key: "mes", label: "Este mês" },
  { key: "mes_passado", label: "Mês passado" },
];
function presetRange(key: PresetKey): { from: string; to: string } {
  const now = new Date();
  if (key === "7d") return { from: isoLocal(addDays(now, -6)), to: isoLocal(now) };
  if (key === "30d") return { from: isoLocal(addDays(now, -29)), to: isoLocal(now) };
  if (key === "mes") return { from: isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)), to: isoLocal(now) };
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: isoLocal(first), to: isoLocal(last) };
}

const STATUS_COLORS = { moving: "#0ea5e9", idle: "#f59e0b", offline: "#f43f5e" } as const;
const CATEGORY = Object.fromEntries(FLEET_EXPENSE_CATEGORIES.map((c) => [c.key, c])) as Record<
  string,
  { key: string; label: string; color: string }
>;

// Veículo "ocupado" = tem motorista ATIVO vinculado (aba Motoristas).
function occupiedDeviceIds(drivers: FleetDriver[]): Set<string> {
  return new Set(drivers.filter((d) => d.active && d.traccarDeviceId).map((d) => String(d.traccarDeviceId)));
}

// ---------------------------------------------------------------------------
// Portão de acesso

function FleetPageGate() {
  const { data: access, isLoading } = useFleetAccess();
  if (isLoading) {
    return (
      <GpsInlineLoader className="h-[70vh]" />
    );
  }
  if (!access?.moduleEnabled || !access.view) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem acesso à Gestão de Frota</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {!access?.moduleEnabled
              ? "Este módulo não está ativo para a sua empresa."
              : "Peça a um administrador para liberar em Usuários → Editar → Permissões → Gestão de Frota."}
          </p>
        </div>
      </div>
    );
  }
  return <FleetPage access={access} />;
}

// ---------------------------------------------------------------------------
// Página

// Locações saiu do menu a pedido (2026-10-04); backend continua existindo.
// Vistorias (2026-10-08): só aparece pra quem tem vistoria.view (admin ou
// funcionário) — cliente frotista não vê vistoria de outras pessoas.
type TabKey = "painel" | "veiculos" | "relatorios" | "motoristas" | "custos" | "manutencao" | "vistorias" | "config";
const TABS: { key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: "painel", label: "Painel", icon: Gauge },
  { key: "veiculos", label: "Veículos", icon: Car },
  { key: "relatorios", label: "Relatórios", icon: FileBarChart },
  { key: "motoristas", label: "Motoristas", icon: UserRound },
  { key: "custos", label: "Custos", icon: DollarSign },
  { key: "manutencao", label: "Manutenção", icon: Wrench },
  { key: "vistorias", label: "Vistorias", icon: ClipboardCheck },
  { key: "config", label: "Configurações", icon: Settings },
];

interface FleetCtx {
  access: FleetAccess;
  owner: string;
  ownerLabel: string;
  vehicles: Vehicle[];
  vehicleName: (id: number | string | null | undefined) => string;
  from: string;
  to: string;
}

function FleetPage({ access }: { access: FleetAccess }) {
  const [tab, setTab] = useState<TabKey>("painel");
  const [owner, setOwner] = useState("");
  const [preset, setPreset] = useState<PresetKey>("30d");
  const { from, to } = presetRange(preset);
  const isTeam = access.scope === "company";
  const { data: owners = [] } = useFleetOwners(isTeam);
  const { data: scopeIds = [], isLoading: idsLoading } = useFleetVehicleIds(owner);
  const { data: fleet = [], isLoading: fleetLoading } = useVehicles();
  const { data: inspectionAccess } = useVehicleInspectionAccess();
  const tabs = TABS.filter((t) => t.key !== "vistorias" || inspectionAccess?.view);

  const vehicles = useMemo(() => {
    const byId = new Map(fleet.map((v) => [v.id, v]));
    return scopeIds.map(
      (id) =>
        byId.get(id) ??
        ({ id, name: `Veículo #${id}`, plate: "", status: "offline", speed: 0, lat: 0, lng: 0, lastConnection: "" } as Vehicle),
    );
  }, [scopeIds, fleet]);
  const vehicleName = useMemo(() => {
    const map = new Map(vehicles.map((v) => [v.id, v.plate ? `${v.name} · ${v.plate}` : v.name]));
    return (id: number | string | null | undefined) => (id == null ? "—" : (map.get(String(id)) ?? `Veículo #${id}`));
  }, [vehicles]);

  const ownerLabel =
    !isTeam
      ? "sua frota"
      : owner === ""
        ? "toda a empresa"
        : owner === "empresa"
          ? "a própria empresa"
          : (owners.find((o) => o.id === owner)?.name ?? "cliente");

  const ctx: FleetCtx = { access, owner, ownerLabel, vehicles, vehicleName, from, to };

  return (
    <div className="min-h-full bg-muted/30">
      <div className="mx-auto max-w-[1400px] space-y-5 p-4 md:p-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-sky-500/15 text-lg">🚛</span>
              Gestão de Frota
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Veículos, motoristas, custos e manutenção — {ownerLabel}
            </p>
          </div>
          {isTeam && <OwnerPicker owners={owners} value={owner} onChange={setOwner} />}
        </header>

        <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-background p-2">
          <nav className="flex flex-1 flex-wrap gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                  tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
                )}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            ))}
          </nav>
          {(tab === "painel" || tab === "veiculos" || tab === "custos") && (
            <div className="flex items-center gap-1 border-l pl-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={cn(
                    "rounded-lg px-2.5 py-1 text-xs font-semibold",
                    preset === p.key ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {idsLoading || fleetLoading ? (
          <div className="flex items-center justify-center gap-2 p-16 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando frota…
          </div>
        ) : (
          <>
            {tab === "painel" && <DashboardTab ctx={ctx} />}
            {tab === "veiculos" && <VehiclesTab ctx={ctx} />}
            {tab === "relatorios" && <FleetVehicleReport vehicles={ctx.vehicles} vehicleName={ctx.vehicleName} />}
            {tab === "motoristas" && <DriversTab ctx={ctx} />}
            {tab === "custos" && <ExpensesTab ctx={ctx} />}
            {tab === "manutencao" && <MaintenanceTab ctx={ctx} />}
            {tab === "vistorias" && <InspectionsTab ctx={ctx} />}
            {tab === "config" && <SettingsTab ctx={ctx} />}
          </>
        )}
      </div>
    </div>
  );
}

function OwnerPicker({
  owners,
  value,
  onChange,
}: {
  owners: { id: string; name: string; email: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? owners.filter((o) => o.name.toLowerCase().includes(s) || o.email.toLowerCase().includes(s)) : owners;
    return list.slice(0, 200);
  }, [owners, q]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar cliente…"
          className="h-9 w-48 rounded-lg border bg-background pl-8 pr-2 text-sm outline-none focus:border-primary"
        />
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 max-w-[280px] rounded-lg border bg-background px-2 text-sm outline-none focus:border-primary"
      >
        <option value="">Toda a empresa</option>
        <option value="empresa">Só da empresa (sem cliente)</option>
        {filtered.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
        {value && value !== "empresa" && !filtered.some((o) => o.id === value) && (
          <option value={value}>{owners.find((o) => o.id === value)?.name ?? "Cliente"}</option>
        )}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Peças visuais

function Kpi({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: ReactNode;
  accent: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-background p-4 shadow-sm">
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: accent }} />
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <span className="grid h-7 w-7 place-items-center rounded-lg" style={{ background: `${accent}22`, color: accent }}>
          <Icon className="h-4 w-4" />
        </span>
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Panel({ title, subtitle, action, children, className }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-2xl border bg-background p-4 shadow-sm", className)}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function EmptyChart({ text }: { text: string }) {
  return <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">{text}</div>;
}

function ChartTooltip({
  active,
  payload,
  label,
  format,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
  format: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
      {label && <div className="mb-1 font-semibold">{label}</div>}
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold tabular-nums">{format(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Métricas por veículo (relatórios do rastreamento + custos)

interface VehicleStats {
  id: string;
  distanceKm: number;
  movingMs: number;
  stoppedMs: number;
  stops: number;
  cost: number;
}

// Km/tempos de poucos veículos direto dos relatórios do rastreamento — só a
// página aberta da aba Veículos (a frota inteira vem do servidor, ver
// useServerFleetStats). `ids` precisa vir memoizado (vira query key).
function useFleetStats(ctx: FleetCtx, summary: FleetSummary | undefined, ids: string[]) {
  const fromIso = new Date(`${ctx.from}T00:00:00`).toISOString();
  const toIso = new Date(`${ctx.to}T23:59:59`).toISOString();
  const reports = useFleetReports(ids, fromIso, toIso, true);

  const stats = useMemo(() => {
    const map = new Map<string, VehicleStats>();
    const get = (id: string) => {
      let s = map.get(id);
      if (!s) {
        s = { id, distanceKm: 0, movingMs: 0, stoppedMs: 0, stops: 0, cost: 0 };
        map.set(id, s);
      }
      return s;
    };
    for (const id of ids) get(id);
    const perDay = new Map<string, { day: string; km: number; moving: number; stopped: number }>();
    const day = (iso: string) => {
      const d = isoLocal(new Date(iso));
      let row = perDay.get(d);
      if (!row) {
        row = { day: d, km: 0, moving: 0, stopped: 0 };
        perDay.set(d, row);
      }
      return row;
    };
    for (const t of reports.data?.trips ?? []) {
      const s = get(t.vehicleId);
      s.distanceKm += t.distanceKm;
      s.movingMs += t.durationMs;
      const r = day(t.startTime);
      r.km += t.distanceKm;
      r.moving += t.durationMs / 3_600_000;
    }
    for (const st of reports.data?.stops ?? []) {
      const s = get(st.vehicleId);
      s.stoppedMs += st.durationMs;
      s.stops += 1;
      day(st.startTime).stopped += st.durationMs / 3_600_000;
    }
    for (const [id, cost] of Object.entries(summary?.expenses.byDevice ?? {})) get(id).cost += cost;

    // Todos os dias do período, inclusive os sem movimento (gráfico contínuo).
    const days: { day: string; label: string; km: number; moving: number; stopped: number }[] = [];
    for (let d = new Date(`${ctx.from}T00:00:00`); isoLocal(d) <= ctx.to; d = addDays(d, 1)) {
      const key = isoLocal(d);
      const r = perDay.get(key);
      days.push({
        day: key,
        label: `${key.slice(8, 10)}/${key.slice(5, 7)}`,
        km: Math.round(r?.km ?? 0),
        moving: Number((r?.moving ?? 0).toFixed(1)),
        stopped: Number((r?.stopped ?? 0).toFixed(1)),
      });
    }
    const all = [...map.values()];
    return {
      byVehicle: map,
      days,
      totalKm: all.reduce((s, v) => s + v.distanceKm, 0),
      totalMoving: all.reduce((s, v) => s + v.movingMs, 0),
      totalStopped: all.reduce((s, v) => s + v.stoppedMs, 0),
      totalStops: all.reduce((s, v) => s + v.stops, 0),
    };
  }, [reports.data, summary, ids, ctx.from, ctx.to]);

  return { stats, loading: reports.isLoading, error: reports.error };
}

// Painel: mesmos números de useFleetStats, mas calculados no servidor
// (GET /fleet-management/stats, 2026-10-02) — a frota inteira no navegador
// levava minutos. `loadingText` mostra o progresso do cálculo.
// `deviceId` = só aquele veículo (seletor do Painel).
function useServerFleetStats(ctx: FleetCtx, summary: FleetSummary | undefined, deviceId?: string) {
  const { data: res, isLoading, error } = useFleetServerStats(ctx.owner, ctx.from, ctx.to, deviceId);
  const stats = useMemo(() => {
    const map = new Map<string, VehicleStats>();
    for (const v of ctx.vehicles) if (!deviceId || v.id === deviceId) map.set(v.id, { id: v.id, distanceKm: 0, movingMs: 0, stoppedMs: 0, stops: 0, cost: 0 });
    for (const [id, s] of Object.entries(res?.data?.byVehicle ?? {})) {
      const row = map.get(id);
      if (row) Object.assign(row, s);
    }
    for (const [id, cost] of Object.entries(summary?.expenses.byDevice ?? {})) {
      const row = map.get(id);
      if (row) row.cost += cost;
    }
    const days = (res?.data?.days ?? []).map((d) => ({
      day: d.day,
      label: `${d.day.slice(8, 10)}/${d.day.slice(5, 7)}`,
      km: Math.round(d.km),
      moving: Number((d.movingMs / 3_600_000).toFixed(1)),
      stopped: Number((d.stoppedMs / 3_600_000).toFixed(1)),
    }));
    const all = [...map.values()];
    return {
      byVehicle: map,
      days,
      totalKm: all.reduce((s, v) => s + v.distanceKm, 0),
      totalMoving: all.reduce((s, v) => s + v.movingMs, 0),
      totalStopped: all.reduce((s, v) => s + v.stoppedMs, 0),
      totalStops: all.reduce((s, v) => s + v.stops, 0),
    };
  }, [res, summary, ctx.vehicles, deviceId]);

  const loading = isLoading || res?.status === "computing" || res?.status === "error" || !!error;
  const loadingText = error
    ? `Erro ao calcular: ${errMsg(error)}`
    : res?.status === "error"
      ? `Erro ao calcular: ${res.error ?? "falha no rastreamento"} — tentando de novo ao recarregar.`
      : res?.status === "computing" && res.progress && res.progress.total > 0
        ? `Calculando no servidor… ${Math.round((res.progress.done / res.progress.total) * 100)}%`
        : "Calculando no servidor…";
  return { stats, loading, loadingText };
}

// ---------------------------------------------------------------------------
// Painel

// Seletor no topo (pedido 2026-10-04): todos os veículos ou um só — KPIs,
// gráficos e custos seguem a escolha. A lista "Desempenho por veículo"
// embaixo é sempre da frota toda; clicar numa linha escolhe o veículo.
function DashboardTab({ ctx }: { ctx: FleetCtx }) {
  const [picked, setPicked] = useState("");
  const vehicleId = picked && ctx.vehicles.some((v) => v.id === picked) ? picked : "";
  const { data: summary } = useFleetSummary(ctx.owner, ctx.from, ctx.to, vehicleId || undefined);
  const { stats, loading, loadingText } = useServerFleetStats(ctx, summary, vehicleId || undefined);
  const shown = vehicleId ? ctx.vehicles.filter((v) => v.id === vehicleId) : ctx.vehicles;
  const scopeLabel = vehicleId ? ctx.vehicleName(vehicleId) : "Soma de todos os veículos";
  const total = shown.length;
  const moving = shown.filter((v) => v.status === "online").length;
  const idle = shown.filter((v) => v.status === "idle").length;
  const offline = total - moving - idle;
  const costTotal = summary?.expenses.total ?? 0;
  const costPerKm = stats.totalKm > 0 ? costTotal / stats.totalKm : 0;
  const { data: drivers = [] } = useFleetList("drivers", ctx.owner);
  const busy = occupiedDeviceIds(drivers);
  const occupied = shown.filter((v) => busy.has(v.id)).length;

  const statusData = [
    { name: "Em movimento", value: moving, color: STATUS_COLORS.moving },
    { name: "Parado", value: idle, color: STATUS_COLORS.idle },
    { name: "Offline", value: offline, color: STATUS_COLORS.offline },
  ];
  const categoryData = Object.entries(summary?.expenses.byCategory ?? {})
    .map(([k, v]) => ({ name: CATEGORY[k]?.label ?? k, value: v, color: CATEGORY[k]?.color ?? "#64748b" }))
    .sort((a, b) => b.value - a.value);
  const ranking = [...stats.byVehicle.values()]
    .filter((v) => v.distanceKm > 0)
    .sort((a, b) => b.distanceKm - a.distanceKm)
    .slice(0, 10)
    .map((v) => ({ name: ctx.vehicleName(v.id), km: Math.round(v.distanceKm) }));
  const monthData = Object.entries(summary?.expenses.byMonth ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([m, v]) => ({ name: `${m.slice(5, 7)}/${m.slice(2, 4)}`, value: v }));

  const alerts: { tone: "red" | "amber"; text: string }[] = [];
  for (const m of summary?.maintenance.overdue ?? [])
    alerts.push({ tone: "red", text: `Manutenção atrasada: ${m.title} — ${ctx.vehicleName(m.traccarDeviceId)} (${brDate(m.scheduledDate)})` });
  for (const d of summary?.drivers.cnhExpired ?? []) alerts.push({ tone: "red", text: `CNH vencida: ${d.name} (${brDate(d.cnhExpiresAt)})` });
  for (const m of summary?.maintenance.upcoming ?? [])
    alerts.push({ tone: "amber", text: `Manutenção em breve: ${m.title} — ${ctx.vehicleName(m.traccarDeviceId)} (${brDate(m.scheduledDate)})` });
  for (const d of summary?.drivers.cnhExpiring ?? []) alerts.push({ tone: "amber", text: `CNH vence em breve: ${d.name} (${brDate(d.cnhExpiresAt)})` });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-background p-2">
        <span className="pl-1 text-xs font-medium text-muted-foreground">Mostrar:</span>
        <DashboardVehiclePicker ctx={ctx} value={vehicleId} onChange={setPicked} />
        {vehicleId && (
          <button
            type="button"
            onClick={() => setPicked("")}
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" /> Ver todos
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Kpi icon={Car} label="Veículos" value={String(total)} hint={`${moving + idle} online agora`} accent="#0ea5e9" />
        <Kpi icon={RouteIcon} label="Km rodados" value={loading ? "…" : `${km.format(stats.totalKm)} km`} hint="no período" accent="#6366f1" />
        <Kpi icon={Clock} label="Em movimento" value={loading ? "…" : hours(stats.totalMoving)} hint="soma da frota" accent="#10b981" />
        <Kpi icon={ParkingCircle} label="Parado" value={loading ? "…" : hours(stats.totalStopped)} hint={`${stats.totalStops} paradas`} accent="#f59e0b" />
        <Kpi icon={DollarSign} label="Custos" value={money(costTotal)} hint="no período" accent="#f43f5e" />
        <Kpi icon={Gauge} label="Custo por km" value={costPerKm ? money(costPerKm) : "—"} hint="custos ÷ km" accent="#8b5cf6" />
        <Kpi icon={KeyRound} label="Ocupados" value={String(occupied)} hint={`${total - occupied} disponíveis`} accent="#14b8a6" />
        <Kpi icon={UserRound} label="Motoristas" value={String(summary?.drivers.active ?? 0)} hint="ativos" accent="#64748b" />
      </div>

      {alerts.length > 0 && (
        <Panel title="Atenção" subtitle="Vencimentos e atrasos da frota">
          <ul className="grid gap-1.5 md:grid-cols-2">
            {alerts.slice(0, 12).map((a, i) => (
              <li
                key={i}
                className={cn(
                  "flex items-start gap-2 rounded-lg border px-3 py-2 text-sm",
                  a.tone === "red"
                    ? "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300",
                )}
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {a.text}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="Status da frota" subtitle="Agora">
          {total === 0 ? (
            <EmptyChart text="Nenhum veículo nesta frota." />
          ) : (
            <div className="relative h-[220px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={90} paddingAngle={3} cornerRadius={5} stroke="none">
                    {statusData.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip format={(v) => `${v} veículos`} />} />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-x-0 top-[78px] text-center">
                <div className="text-2xl font-bold">{total}</div>
                <div className="text-[11px] text-muted-foreground">veículos</div>
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Km rodados por dia" subtitle={scopeLabel} className="lg:col-span-2">
          {loading ? (
            <EmptyChart text={loadingText} />
          ) : (
            <div className="h-[220px]">
              <ResponsiveContainer>
                <BarChart data={stats.days} margin={{ left: -10, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" strokeOpacity={0.1} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip format={(v) => `${km.format(v)} km`} />} />
                  <Bar dataKey="km" name="Km" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="Horas em movimento × parado" subtitle={vehicleId ? `${scopeLabel} — por dia` : "Por dia"} className="lg:col-span-2">
          {loading ? (
            <EmptyChart text={loadingText} />
          ) : (
            <div className="h-[240px]">
              <ResponsiveContainer>
                <AreaChart data={stats.days} margin={{ left: -10, right: 8 }}>
                  <defs>
                    <linearGradient id="gMoving" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="gStopped" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" strokeOpacity={0.1} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} unit="h" />
                  <Tooltip content={<ChartTooltip format={(v) => `${v} h`} />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="moving" name="Em movimento" stroke="#10b981" strokeWidth={2} fill="url(#gMoving)" />
                  <Area type="monotone" dataKey="stopped" name="Parado" stroke="#f59e0b" strokeWidth={2} fill="url(#gStopped)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Custos por categoria" subtitle="No período">
          {categoryData.length === 0 ? (
            <EmptyChart text="Nenhum custo lançado no período." />
          ) : (
            <div className="h-[240px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={categoryData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2} cornerRadius={4} stroke="none">
                    {categoryData.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip format={money} />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {!vehicleId && (
        <Panel title="Ranking de quilometragem" subtitle="Top 10 veículos no período">
          {loading ? (
            <EmptyChart text={loadingText} />
          ) : ranking.length === 0 ? (
            <EmptyChart text="Nenhum deslocamento no período." />
          ) : (
            <div style={{ height: Math.max(180, ranking.length * 30) }}>
              <ResponsiveContainer>
                <BarChart data={ranking} layout="vertical" margin={{ left: 10, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="currentColor" strokeOpacity={0.1} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip format={(v) => `${km.format(v)} km`} />} />
                  <Bar dataKey="km" name="Km" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
        )}

        <Panel title="Custos por mês" subtitle="Lançamentos do período" className={vehicleId ? "lg:col-span-2" : undefined}>
          {monthData.length === 0 ? (
            <EmptyChart text="Nenhum custo lançado no período." />
          ) : (
            <div className="h-[220px]">
              <ResponsiveContainer>
                <BarChart data={monthData} margin={{ left: 10, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" strokeOpacity={0.1} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${km.format(v)}`} />
                  <Tooltip content={<ChartTooltip format={money} />} />
                  <Bar dataKey="value" name="Custos" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <VehicleStatsTable
        ctx={ctx}
        selected={vehicleId}
        onSelect={(id) => {
          setPicked(id);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    </div>
  );
}

// Escolha do veículo do Painel: busca por nome/placa (frota com milhares).
function DashboardVehiclePicker({ ctx, value, onChange }: { ctx: FleetCtx; value: string; onChange: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? ctx.vehicles.filter((v) => v.name.toLowerCase().includes(s) || v.plate.toLowerCase().includes(s)) : ctx.vehicles;
    return [...list].sort(compareVehiclesByStatus).slice(0, 100);
  }, [ctx.vehicles, q]);
  const choose = (id: string) => {
    onChange(id);
    setOpen(false);
    setQ("");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 min-w-[260px] items-center justify-between gap-2 rounded-lg border bg-background px-3 text-sm font-medium hover:bg-muted/50"
        >
          <span className="flex items-center gap-2 truncate">
            <Car className="h-4 w-4 text-muted-foreground" />
            {value ? ctx.vehicleName(value) : `Todos os veículos (${ctx.vehicles.length})`}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-2">
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar nome ou placa…"
            className="h-9 w-full rounded-lg border bg-background pl-8 pr-2 text-sm outline-none focus:border-primary"
          />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {!q && (
            <PickerRow active={!value} onClick={() => choose("")}>
              Todos os veículos ({ctx.vehicles.length})
            </PickerRow>
          )}
          {filtered.map((v) => (
            <PickerRow key={v.id} active={v.id === value} onClick={() => choose(v.id)}>
              <span className="truncate">{v.name}</span>
              {v.plate && <LicensePlate plate={v.plate} size="xs" className="ml-auto" />}
            </PickerRow>
          ))}
          {filtered.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground">Nenhum veículo encontrado.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PickerRow({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted", active && "bg-muted font-medium")}
    >
      <Check className={cn("h-3.5 w-3.5 shrink-0", active ? "opacity-100" : "opacity-0")} />
      {children}
    </button>
  );
}

type SortKey = "name" | "distanceKm" | "movingMs" | "stoppedMs" | "stops" | "cost" | "costPerKm";

// "Desempenho por veículo" do Painel: km/tempos de cada veículo no
// período, da frota inteira (mesma consulta do servidor do Painel).
function VehicleStatsTable({ ctx, selected, onSelect }: { ctx: FleetCtx; selected: string; onSelect: (id: string) => void }) {
  const { data: summary } = useFleetSummary(ctx.owner, ctx.from, ctx.to);
  const { stats, loading, loadingText } = useServerFleetStats(ctx, summary);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "distanceKm", desc: true });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const days = Math.max(1, stats.days.length);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = ctx.vehicles
      .filter((v) => !s || v.name.toLowerCase().includes(s) || v.plate.toLowerCase().includes(s))
      .map((v) => {
        const st = stats.byVehicle.get(v.id) ?? { distanceKm: 0, movingMs: 0, stoppedMs: 0, stops: 0, cost: 0 };
        return { v, ...st, costPerKm: st.distanceKm > 0 && st.cost > 0 ? st.cost / st.distanceKm : 0 };
      });
    const dir = sort.desc ? -1 : 1;
    return list.sort((a, b) =>
      sort.key === "name"
        ? dir * NAME_COLLATOR.compare(a.v.name, b.v.name)
        : dir * (a[sort.key] - b[sort.key]) || NAME_COLLATOR.compare(a.v.name, b.v.name),
    );
  }, [ctx.vehicles, stats.byVehicle, q, sort]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const toggleSort = (key: SortKey) => {
    setSort((cur) => (cur.key === key ? { key, desc: !cur.desc } : { key, desc: key !== "name" }));
    setPage(1);
  };
  const Th = ({ k, label, right }: { k: SortKey; label: string; right?: boolean }) => (
    <th className={cn("px-2 py-2", right && "text-right")}>
      <button type="button" onClick={() => toggleSort(k)} className={cn("inline-flex items-center gap-1 uppercase hover:text-foreground", sort.key === k && "text-foreground")}>
        {label}
        {sort.key === k && (sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
      </button>
    </th>
  );

  return (
    <Panel
      title="Desempenho por veículo"
      subtitle="Km rodados, tempo em movimento e parado de cada veículo no período — clique para ver nos gráficos"
      action={
        <SearchInput
          value={q}
          onChange={(v) => {
            setQ(v);
            setPage(1);
          }}
          placeholder="Nome ou placa…"
        />
      }
    >
      {loading ? (
        <EmptyChart text={loadingText} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs tracking-wide text-muted-foreground">
                  <Th k="name" label="Veículo" />
                  <th className="px-2 py-2 uppercase">Status</th>
                  <Th k="distanceKm" label="Km rodados" right />
                  <th className="px-2 py-2 text-right uppercase">Km/dia</th>
                  <Th k="movingMs" label="Em movimento" right />
                  <Th k="stoppedMs" label="Parado" right />
                  <Th k="stops" label="Paradas" right />
                  <Th k="cost" label="Custos" right />
                  <Th k="costPerKm" label="Custo/km" right />
                </tr>
              </thead>
              <tbody className="divide-y">
                {pageRows.map((r) => {
                  const status = r.v.status === "online" ? "moving" : r.v.status === "idle" ? "idle" : "offline";
                  return (
                    <tr
                      key={r.v.id}
                      onClick={() => onSelect(r.v.id)}
                      className={cn("cursor-pointer hover:bg-muted/40", selected === r.v.id && "bg-primary/5")}
                    >
                      <td className="px-2 py-2">
                        <div className="font-medium">{r.v.name}</div>
                        {r.v.plate && <div className="mt-0.5"><LicensePlate plate={r.v.plate} size="xs" /></div>}
                      </td>
                      <td className="px-2 py-2">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[status] }} />
                          {status === "moving" ? "Em movimento" : status === "idle" ? "Parado" : "Offline"}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right font-medium tabular-nums">{km.format(r.distanceKm)} km</td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{km.format(r.distanceKm / days)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{hours(r.movingMs)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{hours(r.stoppedMs)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{r.stops}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{money(r.cost)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{r.costPerKm ? money(r.costPerKm) : "—"}</td>
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                      Nenhum veículo.
                    </td>
                  </tr>
                )}
              </tbody>
              {!q && rows.length > 1 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold">
                    <td className="px-2 py-2" colSpan={2}>
                      Total da frota
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{km.format(stats.totalKm)} km</td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{km.format(stats.totalKm / days)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{hours(stats.totalMoving)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{hours(stats.totalStopped)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{stats.totalStops}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{money(summary?.expenses.total ?? 0)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {stats.totalKm > 0 && (summary?.expenses.total ?? 0) > 0 ? money((summary?.expenses.total ?? 0) / stats.totalKm) : "—"}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length > 0 && (
            <ListPagination
              page={currentPage}
              pageCount={pageCount}
              pageSize={pageSize}
              total={rows.length}
              onPage={setPage}
              onPageSize={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          )}
        </>
      )}
    </Panel>
  );
}


// ---------------------------------------------------------------------------
// Veículos

// Paginada (pedido 2026-10-02): km/paradas só dos veículos da página aberta,
// por isso a ordem é por nome e não por km (km da frota toda = o Painel).
function VehiclesTab({ ctx }: { ctx: FleetCtx }) {
  const { data: summary } = useFleetSummary(ctx.owner, ctx.from, ctx.to);
  const { data: drivers = [] } = useFleetList("drivers", ctx.owner);
  const busy = useMemo(() => occupiedDeviceIds(drivers), [drivers]);
  const [availability, setAvailability] = useState<"" | "disponivel" | "ocupado">("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return ctx.vehicles
      .filter((v) => !s || v.name.toLowerCase().includes(s) || v.plate.toLowerCase().includes(s))
      .filter((v) => !availability || (availability === "ocupado") === busy.has(v.id))
      // Online primeiro (pedido 2026-10-14), depois parados e offline.
      .sort(compareVehiclesByStatus);
  }, [ctx.vehicles, q, availability, busy]);
  const occupiedCount = ctx.vehicles.filter((v) => busy.has(v.id)).length;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageVehicles = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize],
  );
  const pageIds = useMemo(() => pageVehicles.map((v) => v.id), [pageVehicles]);
  const { stats, loading } = useFleetStats(ctx, summary, pageIds);
  const rows = pageVehicles.map((v) => ({ v, s: stats.byVehicle.get(v.id) }));
  const [editing, setEditing] = useState<Vehicle | null>(null);

  return (
    <Panel
      title={`Veículos (${ctx.vehicles.length})`}
      subtitle="Disponível = sem motorista vinculado · Ocupado = com motorista (aba Motoristas)"
      action={
        <div className="flex flex-wrap items-center gap-2">
          {(
            [
              ["", `Todos (${ctx.vehicles.length})`],
              ["disponivel", `Disponíveis (${ctx.vehicles.length - occupiedCount})`],
              ["ocupado", `Ocupados (${occupiedCount})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key || "todos"}
              type="button"
              onClick={() => {
                setAvailability(key);
                setPage(1);
              }}
              className={cn(
                "rounded-lg px-2.5 py-1 text-xs font-semibold",
                availability === key ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted",
              )}
            >
              {label}
            </button>
          ))}
          <SearchInput
            value={q}
            onChange={(v) => {
              setQ(v);
              setPage(1);
            }}
            placeholder="Nome ou placa…"
          />
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-2 py-2">Veículo</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2">Situação</th>
              <th className="px-2 py-2">Motorista</th>
              <th className="px-2 py-2 text-right">Km</th>
              <th className="px-2 py-2 text-right">Em movimento</th>
              <th className="px-2 py-2 text-right">Parado</th>
              <th className="px-2 py-2 text-right">Paradas</th>
              <th className="px-2 py-2 text-right">Custos</th>
              <th className="px-2 py-2 text-right">Custo/km</th>
              <th className="px-2 py-2 text-right">Km atual</th>
              {ctx.access.manage && <th className="px-2 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map(({ v, s }) => {
              const driver = drivers.find((d) => String(d.traccarDeviceId) === v.id && d.active);
              const status = v.status === "online" ? "moving" : v.status === "idle" ? "idle" : "offline";
              return (
                <tr key={v.id} className="hover:bg-muted/40">
                  <td className="px-2 py-2">
                    <div className="font-medium">{v.name}</div>
                    <div className="mt-0.5"><LicensePlate plate={v.plate} size="xs" /></div>
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[status] }} />
                      {status === "moving" ? "Em movimento" : status === "idle" ? "Parado" : "Offline"}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    {driver ? (
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-300">Ocupado</span>
                    ) : (
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">Disponível</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs">{driver?.name ?? "—"}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{loading ? "…" : km.format(s?.distanceKm ?? 0)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{loading ? "…" : hours(s?.movingMs ?? 0)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{loading ? "…" : hours(s?.stoppedMs ?? 0)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{loading ? "…" : (s?.stops ?? 0)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(s?.cost ?? 0)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {s && s.distanceKm > 0 && s.cost > 0 ? money(s.cost / s.distanceKm) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {v.odometer != null ? `${km.format(v.odometer)} km` : "—"}
                    {v.hours ? <div className="text-[11px] text-muted-foreground">{km.format(v.hours / 3_600_000)} h motor</div> : null}
                  </td>
                  {ctx.access.manage && (
                    <td className="px-2 py-2 text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(v)} aria-label="Editar km" title="Editar km / horímetro">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className="py-10 text-center text-sm text-muted-foreground">
                  Nenhum veículo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {filtered.length > 0 && (
        <ListPagination
          page={currentPage}
          pageCount={pageCount}
          pageSize={pageSize}
          total={filtered.length}
          onPage={setPage}
          onPageSize={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
      )}
      {editing && <OdometerDialog ctx={ctx} vehicle={editing} onClose={() => setEditing(null)} />}
    </Panel>
  );
}

// Lápis da aba Veículos (pedido 2026-10-14): acerta o km (hodômetro) e o
// horímetro do rastreador — a partir daqui ele continua somando sozinho.
function OdometerDialog({ ctx, vehicle, onClose }: { ctx: FleetCtx; vehicle: Vehicle; onClose: () => void }) {
  const qc = useQueryClient();
  const [kmText, setKmText] = useState(vehicle.odometer != null ? String(vehicle.odometer) : "");
  const [hoursText, setHoursText] = useState(vehicle.hours ? String(Math.round(vehicle.hours / 3_600_000)) : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    const kmVal = toNum(kmText.replace(/\./g, ""));
    const hoursVal = toNum(hoursText.replace(/\./g, ""));
    if (kmVal == null && hoursVal == null) return setError("Informe o km ou o horímetro.");
    if ((kmVal != null && (!Number.isFinite(kmVal) || kmVal < 0)) || (hoursVal != null && (!Number.isFinite(hoursVal) || hoursVal < 0)))
      return setError("Valor inválido.");
    setSaving(true);
    setError(null);
    try {
      await fleetManagementService.setOdometer(ctx.owner, vehicle.id, { km: kmVal, hours: hoursVal });
      void qc.invalidateQueries({ queryKey: ["vehicles"] });
      onClose();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar km — {ctx.vehicleName(vehicle.id)}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Km atual do painel (hodômetro)">
            <input className={inputCls} inputMode="numeric" value={kmText} onChange={(e) => setKmText(e.target.value)} placeholder="Ex.: 85320" autoFocus />
          </Field>
          <Field label="Horímetro (horas de motor)">
            <input className={inputCls} inputMode="numeric" value={hoursText} onChange={(e) => setHoursText(e.target.value)} placeholder="Opcional" />
          </Field>
        </div>
        <p className="text-xs text-muted-foreground">
          Coloque o km que está no painel do veículo. O rastreador continua somando a partir dele, e é esse km que dispara os avisos de
          manutenção. Se o rastreador lê o km direto do carro (OBD), ele continua mostrando o valor do carro.
        </p>
        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">Não foi salvo: {error}</div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 w-52 rounded-lg border bg-background pl-8 pr-2 text-sm outline-none focus:border-primary"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulários (campos nativos de propósito: Select/Popover do shadcn dentro
// de Dialog ficam escondidos atrás dele — ver bug de z-index conhecido).

const inputCls = "h-9 w-full rounded-lg border bg-background px-2.5 text-sm outline-none focus:border-primary";

function Field({ label, children, span }: { label: string; children: ReactNode; span?: boolean }) {
  return (
    <label className={cn("space-y-1", span && "sm:col-span-2")}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function VehicleSelect({
  ctx,
  value,
  onChange,
  allowEmpty,
}: {
  ctx: FleetCtx;
  value: string;
  onChange: (v: string) => void;
  allowEmpty?: string;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      {allowEmpty !== undefined ? <option value="">{allowEmpty}</option> : <option value="" disabled>Escolha o veículo</option>}
      {ctx.vehicles.map((v) => (
        <option key={v.id} value={v.id}>
          {ctx.vehicleName(v.id)}
        </option>
      ))}
    </select>
  );
}

function FormDialog({
  open,
  title,
  ctx,
  saving,
  error,
  onClose,
  onSave,
  children,
}: {
  open: boolean;
  title: string;
  ctx: FleetCtx;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {ctx.access.scope === "company" && (
            <p className="text-xs text-muted-foreground">Registro de: {ctx.ownerLabel === "toda a empresa" ? "a própria empresa" : ctx.ownerLabel}</p>
          )}
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">{children}</div>
        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
            Não foi salvo: {error}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Estado comum dos formulários: abre vazio (novo) ou com o registro (editar),
// salva sem popup de confirmação e fecha; erro fica dentro do dialog.
function useEditor<T extends { id: string }, F>(
  entity: "drivers" | "expenses" | "maintenances" | "rentals",
  owner: string,
  toForm: (row: T | null) => F,
  toBody: (form: F) => { body: object } | { error: string },
) {
  const { create, update, remove } = useFleetMutations(entity, owner);
  const [editing, setEditing] = useState<T | null | undefined>(undefined);
  const [form, setForm] = useState<F>(() => toForm(null));
  const [error, setError] = useState<string | null>(null);
  const open = (row: T | null) => {
    setEditing(row);
    setForm(toForm(row));
    setError(null);
  };
  const close = () => setEditing(undefined);
  const save = async () => {
    const built = toBody(form);
    if ("error" in built) {
      setError(built.error);
      return;
    }
    try {
      if (editing) await update.mutateAsync({ id: editing.id, body: built.body });
      else await create.mutateAsync(built.body);
      close();
    } catch (err) {
      setError(errMsg(err));
    }
  };
  const del = async (row: T) => {
    try {
      await remove.mutateAsync(row.id);
    } catch (err) {
      toast.error("Não foi possível excluir", { description: errMsg(err) });
    }
  };
  return {
    isOpen: editing !== undefined,
    isNew: editing === null,
    editing,
    form,
    set: (patch: Partial<F>) => setForm((f) => ({ ...f, ...patch })),
    open,
    close,
    save,
    del,
    error,
    saving: create.isPending || update.isPending,
  };
}

function RowActions({ canManage, onEdit, onDelete }: { canManage: boolean; onEdit: () => void; onDelete: () => void }) {
  const [confirm, setConfirm] = useState(false);
  if (!canManage) return null;
  if (confirm) {
    return (
      <div className="flex items-center justify-end gap-1">
        <span className="text-xs text-rose-600">Excluir?</span>
        <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
          Não
        </Button>
        <Button size="sm" variant="destructive" onClick={onDelete}>
          Sim
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-end gap-1">
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} aria-label="Editar">
        <Pencil className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => setConfirm(true)} aria-label="Excluir">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function AddButton({ canManage, label, onClick }: { canManage: boolean; label: string; onClick: () => void }) {
  if (!canManage) return null;
  return (
    <Button size="sm" onClick={onClick}>
      <Plus className="h-4 w-4" /> {label}
    </Button>
  );
}

function ListState({ loading, empty, text }: { loading: boolean; empty: boolean; text: string }) {
  if (loading)
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
      </div>
    );
  if (empty) return <div className="py-10 text-center text-sm text-muted-foreground">{text}</div>;
  return null;
}

// ---------------------------------------------------------------------------
// Motoristas

interface DriverForm {
  name: string;
  cpf: string;
  phone: string;
  email: string;
  cnhNumber: string;
  cnhCategory: string;
  cnhExpiresAt: string;
  traccarDeviceId: string;
  notes: string;
  bio: string;
  active: boolean;
}

function CnhBadge({ date }: { date: string | null }) {
  const d = daysUntil(date);
  if (d == null) return <span className="text-xs text-muted-foreground">—</span>;
  const cls =
    d < 0
      ? "bg-rose-500/15 text-rose-700 dark:text-rose-300"
      : d <= 30
        ? "bg-amber-500/15 text-amber-800 dark:text-amber-300"
        : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", cls)}>
      {brDate(date)} {d < 0 ? "· vencida" : d <= 30 ? `· ${d} dias` : ""}
    </span>
  );
}

function DriversTab({ ctx }: { ctx: FleetCtx }) {
  const { data: drivers = [], isLoading } = useFleetList("drivers", ctx.owner);
  const [q, setQ] = useState("");
  // Perfil aberto (estilo Instagram, 2026-10-14): foto, capa, números e
  // documentos / contratos / recorrência do motorista.
  const [expanded, setExpanded] = useState<string | null>(null);
  const ed = useEditor<FleetDriver, DriverForm>(
    "drivers",
    ctx.owner,
    (d) => ({
      name: d?.name ?? "",
      cpf: d?.cpf ?? "",
      phone: d?.phone ?? "",
      email: d?.email ?? "",
      cnhNumber: d?.cnhNumber ?? "",
      cnhCategory: d?.cnhCategory ?? "",
      cnhExpiresAt: d?.cnhExpiresAt ?? "",
      traccarDeviceId: d?.traccarDeviceId ? String(d.traccarDeviceId) : "",
      notes: d?.notes ?? "",
      bio: d?.bio ?? "",
      active: d?.active ?? true,
    }),
    (f) =>
      !f.name.trim()
        ? { error: "Informe o nome do motorista." }
        : {
            body: {
              name: f.name.trim(),
              cpf: f.cpf.trim() || null,
              phone: f.phone.trim() || null,
              email: f.email.trim() || null,
              cnhNumber: f.cnhNumber.trim() || null,
              cnhCategory: f.cnhCategory.trim().toUpperCase() || null,
              cnhExpiresAt: f.cnhExpiresAt || null,
              traccarDeviceId: f.traccarDeviceId ? Number(f.traccarDeviceId) : null,
              notes: f.notes.trim() || null,
              bio: f.bio.trim() || null,
              active: f.active,
            },
          },
  );
  const openDriver = expanded ? (drivers.find((d) => d.id === expanded) ?? null) : null;
  // Vistoria por link (2026-10-23): só quem vê as vistorias (escritório).
  const { data: inspectionAccess } = useVehicleInspectionAccess();
  const [inspectionDriver, setInspectionDriver] = useState<FleetDriver | null>(null);
  const sendInspection = async (d: FleetDriver) => {
    if (!inspectionLinkSkipConfirm() || !d.phone || !d.traccarDeviceId) {
      setInspectionDriver(d);
      return;
    }
    try {
      const r = await sendInspectionLinkDirect(d);
      if (r.whatsapp?.sent) toast.success(`Vistoria enviada para ${d.name.split(" ")[0]} — código ${r.code}`);
      else setInspectionDriver(d);
    } catch {
      setInspectionDriver(d);
    }
  };
  const rows = drivers.filter((d) => {
    const s = q.trim().toLowerCase();
    return !s || d.name.toLowerCase().includes(s) || (d.cpf ?? "").includes(s) || (d.cnhNumber ?? "").includes(s);
  });

  return (
    <Panel
      title={`Motoristas (${drivers.length})`}
      subtitle="Toque no motorista para ver o perfil, trocar a foto e a capa"
      action={
        <div className="flex items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Nome, CPF ou CNH…" />
          <AddButton canManage={ctx.access.manage} label="Novo motorista" onClick={() => ed.open(null)} />
        </div>
      }
    >
      <ListState loading={isLoading} empty={!rows.length} text="Nenhum motorista cadastrado." />
      {rows.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {rows.map((d) => (
            <DriverCard
              key={d.id}
              driver={d}
              vehicleName={ctx.vehicleName}
              onOpen={() => setExpanded(d.id)}
              onInspection={inspectionAccess?.view ? () => void sendInspection(d) : undefined}
            />
          ))}
        </div>
      )}
      {inspectionDriver && (
        <InspectionLinkDialog driver={inspectionDriver} vehicles={ctx.vehicles} onClose={() => setInspectionDriver(null)} />
      )}
      {openDriver && (
        <DriverProfileDialog
          driver={openDriver}
          owner={ctx.owner}
          canManage={ctx.access.manage}
          vehicles={ctx.vehicles}
          vehicleName={ctx.vehicleName}
          onClose={() => setExpanded(null)}
          onEdit={() => {
            setExpanded(null);
            ed.open(openDriver);
          }}
          onDelete={() => {
            setExpanded(null);
            void ed.del(openDriver);
          }}
        />
      )}

      <FormDialog open={ed.isOpen} title={ed.isNew ? "Novo motorista" : "Editar motorista"} ctx={ctx} saving={ed.saving} error={ed.error} onClose={ed.close} onSave={ed.save}>
        <Field label="Nome *" span>
          <input className={inputCls} value={ed.form.name} onChange={(e) => ed.set({ name: e.target.value })} />
        </Field>
        <Field label="CPF">
          <input className={inputCls} value={ed.form.cpf} onChange={(e) => ed.set({ cpf: e.target.value })} />
        </Field>
        <Field label="Telefone">
          <input className={inputCls} value={ed.form.phone} onChange={(e) => ed.set({ phone: e.target.value })} />
        </Field>
        <Field label="E-mail" span>
          <input className={inputCls} type="email" value={ed.form.email} onChange={(e) => ed.set({ email: e.target.value })} />
        </Field>
        <Field label="Nº da CNH">
          <input className={inputCls} value={ed.form.cnhNumber} onChange={(e) => ed.set({ cnhNumber: e.target.value })} />
        </Field>
        <Field label="Categoria">
          <select className={inputCls} value={ed.form.cnhCategory} onChange={(e) => ed.set({ cnhCategory: e.target.value })}>
            <option value="">—</option>
            {["A", "B", "AB", "C", "D", "E", "AC", "AD", "AE"].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Validade da CNH">
          <input className={inputCls} type="date" value={ed.form.cnhExpiresAt} onChange={(e) => ed.set({ cnhExpiresAt: e.target.value })} />
        </Field>
        <Field label="Veículo que dirige">
          <VehicleSelect ctx={ctx} value={ed.form.traccarDeviceId} onChange={(v) => ed.set({ traccarDeviceId: v })} allowEmpty="Nenhum" />
        </Field>
        <Field label="Bio do perfil (aparece embaixo do nome)" span>
          <textarea
            className={cn(inputCls, "h-16 py-2")}
            maxLength={300}
            placeholder="Ex.: Motorista desde 2019 · rota Zona Sul · turno da manhã"
            value={ed.form.bio}
            onChange={(e) => ed.set({ bio: e.target.value })}
          />
        </Field>
        <Field label="Observações (internas)" span>
          <textarea className={cn(inputCls, "h-20 py-2")} value={ed.form.notes} onChange={(e) => ed.set({ notes: e.target.value })} />
        </Field>
        <p className="text-xs text-muted-foreground sm:col-span-2">A foto e a capa você coloca abrindo o perfil do motorista (ícone da câmera).</p>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={ed.form.active} onChange={(e) => ed.set({ active: e.target.checked })} />
          Motorista ativo
        </label>
      </FormDialog>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Custos

interface ExpenseForm {
  category: string;
  amount: string;
  date: string;
  traccarDeviceId: string;
  driverId: string;
  description: string;
  odometerKm: string;
  liters: string;
  paid: boolean;
}

function ExpensesTab({ ctx }: { ctx: FleetCtx }) {
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const { data: expenses = [], isLoading } = useFleetList("expenses", ctx.owner, {
    from: ctx.from,
    to: ctx.to,
    deviceId: vehicleFilter || undefined,
  });
  const { data: drivers = [] } = useFleetList("drivers", ctx.owner);
  const ed = useEditor<FleetExpense, ExpenseForm>(
    "expenses",
    ctx.owner,
    (e) => ({
      category: e?.category ?? "combustivel",
      amount: e ? String(e.amount).replace(".", ",") : "",
      date: e?.date ?? isoLocal(new Date()),
      traccarDeviceId: e?.traccarDeviceId ? String(e.traccarDeviceId) : "",
      driverId: e?.driverId ?? "",
      description: e?.description ?? "",
      odometerKm: e?.odometerKm != null ? String(e.odometerKm) : "",
      liters: e?.liters != null ? String(e.liters).replace(".", ",") : "",
      paid: e?.paid ?? true,
    }),
    (f) => {
      const amount = toNum(f.amount);
      if (amount == null || !Number.isFinite(amount) || amount <= 0) return { error: "Informe um valor maior que zero." };
      if (!f.date) return { error: "Informe a data." };
      const liters = toNum(f.liters);
      const odo = toNum(f.odometerKm);
      return {
        body: {
          category: f.category,
          amount,
          date: f.date,
          traccarDeviceId: f.traccarDeviceId ? Number(f.traccarDeviceId) : null,
          driverId: f.driverId || null,
          description: f.description.trim() || null,
          odometerKm: odo != null && Number.isFinite(odo) ? Math.round(odo) : null,
          liters: liters != null && Number.isFinite(liters) ? liters : null,
          paid: f.paid,
        },
      };
    },
  );
  const rows = categoryFilter ? expenses.filter((e) => e.category === categoryFilter) : expenses;
  const total = rows.reduce((s, e) => s + e.amount, 0);
  const totals = FLEET_EXPENSE_CATEGORIES.map((c) => ({
    ...c,
    value: expenses.filter((e) => e.category === c.key).reduce((s, e) => s + e.amount, 0),
  })).filter((c) => c.value > 0);

  return (
    <div className="space-y-4">
      {totals.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {totals.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategoryFilter((cur) => (cur === c.key ? "" : c.key))}
              className={cn(
                "flex items-center gap-2 rounded-xl border bg-background px-3 py-2 text-sm shadow-sm transition-colors",
                categoryFilter === c.key && "ring-2 ring-primary",
              )}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
              {c.label}
              <span className="font-semibold tabular-nums">{money(c.value)}</span>
            </button>
          ))}
        </div>
      )}
      <Panel
        title={`Custos — ${money(total)}`}
        subtitle={`${rows.length} lançamento(s) de ${brDate(ctx.from)} a ${brDate(ctx.to)}`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)} className="h-8 max-w-[220px] rounded-lg border bg-background px-2 text-sm">
              <option value="">Todos os veículos</option>
              {ctx.vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {ctx.vehicleName(v.id)}
                </option>
              ))}
            </select>
            <AddButton canManage={ctx.access.manage} label="Lançar custo" onClick={() => ed.open(null)} />
          </div>
        }
      >
        <ListState loading={isLoading} empty={!rows.length} text="Nenhum custo lançado no período." />
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-2">Data</th>
                  <th className="px-2 py-2">Categoria</th>
                  <th className="px-2 py-2">Veículo</th>
                  <th className="px-2 py-2">Descrição</th>
                  <th className="px-2 py-2 text-right">Valor</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((e) => (
                  <tr key={e.id} className="hover:bg-muted/40">
                    <td className="px-2 py-2 text-xs tabular-nums">{brDate(e.date)}</td>
                    <td className="px-2 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY[e.category]?.color }} />
                        {CATEGORY[e.category]?.label ?? e.category}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-xs">{ctx.vehicleName(e.traccarDeviceId)}</td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      {e.description ?? ""}
                      {e.liters ? ` · ${String(e.liters).replace(".", ",")} L` : ""}
                      {e.odometerKm ? ` · ${km.format(e.odometerKm)} km` : ""}
                      {!e.paid && <span className="ml-1 rounded bg-amber-500/15 px-1.5 text-[10px] font-semibold text-amber-800 dark:text-amber-300">A PAGAR</span>}
                      {e.maintenanceId && <span className="ml-1 text-[10px]">(da manutenção)</span>}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums">{money(e.amount)}</td>
                    <td className="px-2 py-2">
                      {/* Custo gerado por manutenção é editado pela própria manutenção. */}
                      <RowActions canManage={ctx.access.manage && !e.maintenanceId} onEdit={() => ed.open(e)} onDelete={() => ed.del(e)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <FormDialog open={ed.isOpen} title={ed.isNew ? "Lançar custo" : "Editar custo"} ctx={ctx} saving={ed.saving} error={ed.error} onClose={ed.close} onSave={ed.save}>
        <Field label="Categoria *">
          <select className={inputCls} value={ed.form.category} onChange={(e) => ed.set({ category: e.target.value })}>
            {FLEET_EXPENSE_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Valor (R$) *">
          <input className={inputCls} inputMode="decimal" placeholder="0,00" value={ed.form.amount} onChange={(e) => ed.set({ amount: e.target.value })} />
        </Field>
        <Field label="Data *">
          <input className={inputCls} type="date" value={ed.form.date} onChange={(e) => ed.set({ date: e.target.value })} />
        </Field>
        <Field label="Veículo">
          <VehicleSelect ctx={ctx} value={ed.form.traccarDeviceId} onChange={(v) => ed.set({ traccarDeviceId: v })} allowEmpty="Nenhum (custo geral)" />
        </Field>
        <Field label="Motorista">
          <select className={inputCls} value={ed.form.driverId} onChange={(e) => ed.set({ driverId: e.target.value })}>
            <option value="">—</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Hodômetro (km)">
          <input className={inputCls} inputMode="numeric" value={ed.form.odometerKm} onChange={(e) => ed.set({ odometerKm: e.target.value })} />
        </Field>
        {ed.form.category === "combustivel" && (
          <Field label="Litros">
            <input className={inputCls} inputMode="decimal" value={ed.form.liters} onChange={(e) => ed.set({ liters: e.target.value })} />
          </Field>
        )}
        <Field label="Descrição" span>
          <input className={inputCls} value={ed.form.description} onChange={(e) => ed.set({ description: e.target.value })} />
        </Field>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={ed.form.paid} onChange={(e) => ed.set({ paid: e.target.checked })} />
          Já pago
        </label>
      </FormDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manutenção

interface MaintenanceForm {
  traccarDeviceId: string;
  type: string;
  title: string;
  status: string;
  scheduledDate: string;
  scheduledKm: string;
  doneAt: string;
  doneKm: string;
  cost: string;
  provider: string;
  notes: string;
  intervalKm: string;
  intervalDays: string;
  notifyWhatsapp: boolean;
  notifyPush: boolean;
  notifyPhone: string;
  notifyDaysBefore: string;
  notifyKmBefore: string;
}

// Último WhatsApp usado no aviso — já vem preenchido na próxima manutenção.
const NOTIFY_PHONE_KEY = "gpsdrive.fleet.notifyPhone";
function lastNotifyPhone(): string {
  try {
    return localStorage.getItem(NOTIFY_PHONE_KEY) ?? "";
  } catch {
    return "";
  }
}

const MAINT_STATUS: Record<string, { label: string; cls: string }> = {
  agendada: { label: "Agendada", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-300" },
  concluida: { label: "Concluída", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
  cancelada: { label: "Cancelada", cls: "bg-muted text-muted-foreground" },
};

function MaintenanceTab({ ctx }: { ctx: FleetCtx }) {
  const { data: items = [], isLoading } = useFleetList("maintenances", ctx.owner);
  const { update } = useFleetMutations("maintenances", ctx.owner);
  const [status, setStatus] = useState<string>("agendada");
  const ed = useEditor<FleetMaintenance, MaintenanceForm>(
    "maintenances",
    ctx.owner,
    (m) => ({
      traccarDeviceId: m ? String(m.traccarDeviceId) : "",
      type: m?.type ?? "preventiva",
      title: m?.title ?? "",
      status: m?.status ?? "agendada",
      scheduledDate: m?.scheduledDate ?? "",
      scheduledKm: m?.scheduledKm != null ? String(m.scheduledKm) : "",
      doneAt: m?.doneAt ?? "",
      doneKm: m?.doneKm != null ? String(m.doneKm) : "",
      cost: m?.cost != null ? String(m.cost).replace(".", ",") : "",
      provider: m?.provider ?? "",
      notes: m?.notes ?? "",
      intervalKm: m?.intervalKm != null ? String(m.intervalKm) : "",
      intervalDays: m?.intervalDays != null ? String(m.intervalDays) : "",
      notifyWhatsapp: m?.notifyWhatsapp ?? false,
      notifyPush: m ? m.notifyPush : true,
      notifyPhone: m?.notifyPhone ?? lastNotifyPhone(),
      notifyDaysBefore: String(m?.notifyDaysBefore ?? 7),
      notifyKmBefore: String(m?.notifyKmBefore ?? 500),
    }),
    (f) => {
      if (!f.traccarDeviceId) return { error: "Escolha o veículo." };
      if (!f.title.trim()) return { error: "Informe o serviço (ex.: troca de óleo)." };
      const phoneDigits = f.notifyPhone.replace(/\D/g, "");
      if (f.notifyWhatsapp && phoneDigits.length < 10) return { error: "Informe o WhatsApp (com DDD) para receber o aviso." };
      if (f.notifyWhatsapp) {
        try {
          localStorage.setItem(NOTIFY_PHONE_KEY, f.notifyPhone.trim());
        } catch {
          /* sem storage: só não lembra */
        }
      }
      const cost = toNum(f.cost);
      if (cost != null && (!Number.isFinite(cost) || cost < 0)) return { error: "Custo inválido." };
      const int = (v: string) => {
        const n = toNum(v);
        return n != null && Number.isFinite(n) ? Math.round(n) : null;
      };
      return {
        body: {
          traccarDeviceId: Number(f.traccarDeviceId),
          type: f.type,
          title: f.title.trim(),
          status: f.status,
          scheduledDate: f.scheduledDate || null,
          scheduledKm: int(f.scheduledKm),
          doneAt: f.status === "concluida" ? f.doneAt || isoLocal(new Date()) : f.doneAt || null,
          doneKm: int(f.doneKm),
          cost,
          provider: f.provider.trim() || null,
          notes: f.notes.trim() || null,
          intervalKm: int(f.intervalKm) || null,
          intervalDays: int(f.intervalDays) || null,
          notifyWhatsapp: f.notifyWhatsapp,
          notifyPush: f.notifyPush,
          notifyPhone: phoneDigits || null,
          notifyDaysBefore: int(f.notifyDaysBefore) ?? 7,
          notifyKmBefore: int(f.notifyKmBefore) ?? 500,
        },
      };
    },
  );
  const { data: catalog } = useFleetMaintenanceServices(ctx.owner);
  const services = catalog?.items ?? [];
  const vehicleKm = (id: string) => ctx.vehicles.find((v) => v.id === id)?.odometer ?? null;
  // Escolheu um serviço do catálogo: preenche tipo + intervalo e, se for
  // agendar, já sugere a data/km da próxima a partir de hoje/km atual.
  const pickService = (name: string) => {
    const svc = services.find((x) => x.name === name);
    if (!svc) return ed.set({ title: name });
    const kmNow = vehicleKm(ed.form.traccarDeviceId);
    const patch: Partial<MaintenanceForm> = {
      title: svc.name,
      type: svc.type,
      intervalKm: svc.intervalKm ? String(svc.intervalKm) : "",
      intervalDays: svc.intervalDays ? String(svc.intervalDays) : "",
    };
    if (ed.form.status === "agendada") {
      if (svc.intervalKm && kmNow != null && !ed.form.scheduledKm) patch.scheduledKm = String(kmNow + svc.intervalKm);
      if (svc.intervalDays && !ed.form.scheduledDate) patch.scheduledDate = isoLocal(addDays(new Date(), svc.intervalDays));
    }
    ed.set(patch);
  };
  const formKm = vehicleKm(ed.form.traccarDeviceId);
  const counts = { agendada: 0, concluida: 0, cancelada: 0 } as Record<string, number>;
  for (const m of items) counts[m.status] = (counts[m.status] ?? 0) + 1;
  const rows = items.filter((m) => !status || m.status === status);

  const conclude = async (m: FleetMaintenance) => {
    try {
      await update.mutateAsync({ id: m.id, body: { status: "concluida", doneAt: isoLocal(new Date()) } });
    } catch (err) {
      toast.error("Não foi possível concluir", { description: errMsg(err) });
    }
  };

  return (
    <Panel
      title="Manutenção"
      subtitle="Preventivas e corretivas. Ao concluir com custo, ele entra automaticamente em Custos."
      action={
        <div className="flex flex-wrap items-center gap-2">
          {(["agendada", "concluida", "cancelada", ""] as const).map((s) => (
            <button
              key={s || "todas"}
              type="button"
              onClick={() => setStatus(s)}
              className={cn("rounded-lg px-2.5 py-1 text-xs font-semibold", status === s ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted")}
            >
              {s ? `${MAINT_STATUS[s].label} (${counts[s] ?? 0})` : `Todas (${items.length})`}
            </button>
          ))}
          <AddButton canManage={ctx.access.manage} label="Nova manutenção" onClick={() => ed.open(null)} />
        </div>
      }
    >
      <ListState loading={isLoading} empty={!rows.length} text="Nenhuma manutenção aqui." />
      {rows.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((m) => {
            const d = m.status === "agendada" ? daysUntil(m.scheduledDate) : null;
            return (
              <div key={m.id} className={cn("rounded-xl border p-3", d != null && d < 0 && "border-rose-500/40 bg-rose-500/5")}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{m.title}</div>
                    <div className="truncate text-xs text-muted-foreground">{ctx.vehicleName(m.traccarDeviceId)}</div>
                  </div>
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium", MAINT_STATUS[m.status]?.cls)}>
                    {MAINT_STATUS[m.status]?.label}
                  </span>
                </div>
                <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                  <div className="capitalize">{m.type}</div>
                  {m.status === "agendada" ? (
                    <div className={cn(d != null && d < 0 && "font-semibold text-rose-600")}>
                      Agendada: {brDate(m.scheduledDate)}
                      {m.scheduledKm ? ` · aos ${km.format(m.scheduledKm)} km` : ""}
                      {d != null && (d < 0 ? ` · atrasada ${-d} dia(s)` : d === 0 ? " · hoje" : ` · em ${d} dia(s)`)}
                    </div>
                  ) : (
                    <div>
                      Feita: {brDate(m.doneAt)}
                      {m.doneKm ? ` · ${km.format(m.doneKm)} km` : ""}
                    </div>
                  )}
                  {m.provider && <div>Oficina: {m.provider}</div>}
                  {(m.intervalKm || m.intervalDays) && (
                    <div>
                      Repete a cada {[m.intervalKm ? `${km.format(m.intervalKm)} km` : null, m.intervalDays ? `${m.intervalDays} dias` : null].filter(Boolean).join(" ou ")}
                    </div>
                  )}
                  {m.status === "agendada" && (m.notifyWhatsapp || m.notifyPush) && (
                    <div className="flex flex-wrap items-center gap-1 pt-0.5">
                      {m.notifyWhatsapp && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                          <MessageCircle className="h-3 w-3" /> WhatsApp
                        </span>
                      )}
                      {m.notifyPush && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300">
                          <Bell className="h-3 w-3" /> App
                        </span>
                      )}
                      {m.notifiedAt && <span className="text-[10px]">· avisado em {brDate(m.notifiedAt)}</span>}
                    </div>
                  )}
                  {m.cost != null && <div className="font-semibold text-foreground">{money(m.cost)}</div>}
                </div>
                {ctx.access.manage && (
                  <div className="mt-2 flex items-center justify-between border-t pt-2">
                    {m.status === "agendada" ? (
                      <Button size="sm" variant="outline" onClick={() => conclude(m)} disabled={update.isPending}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Concluir
                      </Button>
                    ) : (
                      <span />
                    )}
                    <RowActions canManage onEdit={() => ed.open(m)} onDelete={() => ed.del(m)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <FormDialog open={ed.isOpen} title={ed.isNew ? "Nova manutenção" : "Editar manutenção"} ctx={ctx} saving={ed.saving} error={ed.error} onClose={ed.close} onSave={ed.save}>
        <Field label="Veículo *">
          <VehicleSelect ctx={ctx} value={ed.form.traccarDeviceId} onChange={(v) => ed.set({ traccarDeviceId: v })} />
        </Field>
        <Field label="Tipo">
          <select className={inputCls} value={ed.form.type} onChange={(e) => ed.set({ type: e.target.value })}>
            <option value="preventiva">Preventiva</option>
            <option value="corretiva">Corretiva</option>
          </select>
        </Field>
        <Field label="Serviço *" span>
          {/* Catálogo inteiro num <select> (antes eram só os 10 primeiros
              como atalho + datalist, e os serviços novos do fim da lista
              não apareciam — 2026-10-19). */}
          {services.length > 0 && (
            <select
              className={`${inputCls} mb-1.5`}
              value={services.some((x) => x.name === ed.form.title) ? ed.form.title : ""}
              onChange={(e) => e.target.value && pickService(e.target.value)}
            >
              <option value="">Escolher da lista de serviços…</option>
              {services.map((x) => (
                <option key={x.name} value={x.name}>
                  {x.name}
                </option>
              ))}
            </select>
          )}
          <input
            className={inputCls}
            placeholder="Ou escreva o serviço (ex.: troca de óleo)"
            value={ed.form.title}
            onChange={(e) => pickService(e.target.value)}
          />
        </Field>
        <Field label="Situação">
          <select className={inputCls} value={ed.form.status} onChange={(e) => ed.set({ status: e.target.value })}>
            <option value="agendada">Agendada</option>
            <option value="concluida">Concluída</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </Field>
        <Field label="Oficina / fornecedor">
          <input className={inputCls} value={ed.form.provider} onChange={(e) => ed.set({ provider: e.target.value })} />
        </Field>
        <Field label="Data agendada">
          <input className={inputCls} type="date" value={ed.form.scheduledDate} onChange={(e) => ed.set({ scheduledDate: e.target.value })} />
        </Field>
        <Field label={`Ou ao atingir (km)${formKm != null ? ` · hoje: ${km.format(formKm)} km` : ""}`}>
          <input className={inputCls} inputMode="numeric" value={ed.form.scheduledKm} onChange={(e) => ed.set({ scheduledKm: e.target.value })} />
        </Field>
        <Field label="Data em que foi feita">
          <input className={inputCls} type="date" value={ed.form.doneAt} onChange={(e) => ed.set({ doneAt: e.target.value })} />
        </Field>
        <Field label="Km quando foi feita">
          <input className={inputCls} inputMode="numeric" value={ed.form.doneKm} onChange={(e) => ed.set({ doneKm: e.target.value })} />
        </Field>
        <Field label="Custo (R$)">
          <input className={inputCls} inputMode="decimal" placeholder="0,00" value={ed.form.cost} onChange={(e) => ed.set({ cost: e.target.value })} />
        </Field>
        <Field label="Observações" span>
          <textarea className={cn(inputCls, "h-20 py-2")} value={ed.form.notes} onChange={(e) => ed.set({ notes: e.target.value })} />
        </Field>

        <div className="space-y-3 rounded-xl border bg-muted/30 p-3 sm:col-span-2">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Bell className="h-4 w-4" /> Próxima manutenção e aviso
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Repetir a cada (km)">
              <input className={inputCls} inputMode="numeric" placeholder="Ex.: 10000" value={ed.form.intervalKm} onChange={(e) => ed.set({ intervalKm: e.target.value })} />
            </Field>
            <Field label="Ou a cada (dias)">
              <input className={inputCls} inputMode="numeric" placeholder="Ex.: 180" value={ed.form.intervalDays} onChange={(e) => ed.set({ intervalDays: e.target.value })} />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">
            Ao concluir, a próxima já fica agendada sozinha (km de quando foi feita + intervalo, ou a data + dias), com o mesmo aviso.
          </p>
          <div className="text-xs font-medium text-muted-foreground">Onde quer ser avisado</div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => ed.set({ notifyWhatsapp: !ed.form.notifyWhatsapp })}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium",
                ed.form.notifyWhatsapp ? "border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:text-emerald-200" : "text-muted-foreground",
              )}
            >
              {ed.form.notifyWhatsapp ? <Check className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />} WhatsApp
            </button>
            <button
              type="button"
              onClick={() => ed.set({ notifyPush: !ed.form.notifyPush })}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium",
                ed.form.notifyPush ? "border-sky-500 bg-sky-500/15 text-sky-800 dark:text-sky-200" : "text-muted-foreground",
              )}
            >
              {ed.form.notifyPush ? <Check className="h-4 w-4" /> : <Bell className="h-4 w-4" />} Notificação no app
            </button>
          </div>
          {ed.form.notifyWhatsapp && (
            <Field label="WhatsApp que recebe o aviso">
              <input className={inputCls} inputMode="tel" placeholder="(11) 99999-0000" value={ed.form.notifyPhone} onChange={(e) => ed.set({ notifyPhone: e.target.value })} />
            </Field>
          )}
          {(ed.form.notifyWhatsapp || ed.form.notifyPush) && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Avisar quantos dias antes">
                <input className={inputCls} inputMode="numeric" value={ed.form.notifyDaysBefore} onChange={(e) => ed.set({ notifyDaysBefore: e.target.value })} />
              </Field>
              <Field label="Avisar quantos km antes">
                <input className={inputCls} inputMode="numeric" value={ed.form.notifyKmBefore} onChange={(e) => ed.set({ notifyKmBefore: e.target.value })} />
              </Field>
            </div>
          )}
          {(ed.form.notifyWhatsapp || ed.form.notifyPush) && (
            <p className="text-xs text-muted-foreground">
              Avisa duas vezes: quando estiver chegando (dias/km antes) e quando chegar a data ou o km. O WhatsApp sai pelo WhatsApp conectado da
              empresa; a notificação vai para o app/navegador de quem criou{ctx.access.scope === "company" ? " e do cliente dono do veículo" : ""}.
            </p>
          )}
        </div>
      </FormDialog>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Configurações

// Configurações DA FROTA (pedido 2026-10-04): integração e modelos de
// contrato são do dono da frota, separados dos da empresa (Administração).
// Cliente frotista = os dele; equipe = do cliente escolhido no topo, ou da
// própria empresa quando nenhum cliente está escolhido.
// ---------------------------------------------------------------------------
// Vistorias — feitas pelos motoristas no app + checklist da empresa

function InspectionsTab({ ctx }: { ctx: FleetCtx }) {
  const [view, setView] = useState<"feitas" | "links" | "checklist">("feitas");
  // Cliente escolhido no topo: só as vistorias dos veículos dele.
  const deviceIds = useMemo(
    () => (ctx.owner === "" ? null : new Set(ctx.vehicles.map((v) => String(v.id)))),
    [ctx.owner, ctx.vehicles],
  );
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border bg-background p-1">
        {(
          [
            ["feitas", "Vistorias feitas"],
            ["links", "Links enviados"],
            ["checklist", "Checklist"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium",
              view === key ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {view === "feitas" ? (
        <InspectionsBrowser canViewAll deviceIds={deviceIds} />
      ) : view === "links" ? (
        <InspectionLinksList />
      ) : (
        <InspectionChecklistEditor />
      )}
    </div>
  );
}

function SettingsTab({ ctx }: { ctx: FleetCtx }) {
  const isTeam = ctx.access.scope === "company";
  const ownerText = !isTeam ? "sua frota" : ctx.owner === "" ? "a própria empresa" : ctx.ownerLabel;
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {isTeam && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-2.5 text-sm text-sky-900 dark:text-sky-200 lg:col-span-2">
          Configurações de: <b>{ownerText}</b>. Cada cliente frotista tem as próprias — escolha o cliente no seletor do topo.
        </div>
      )}
      <AsaasSettingsPanel ctx={ctx} />
      <Panel title="Quem acessa a Gestão de Frota">
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• <b className="text-foreground">Administrador</b>: acesso total, vê a frota de todos os clientes.</li>
          <li>• <b className="text-foreground">Funcionário</b>: precisa da permissão “Gestão de Frota” (ver / cadastrar).</li>
          <li>
            • <b className="text-foreground">Cliente frotista</b>: liberado em Usuários → Editar → Permissões → Gestão de Frota. Ele vê só os
            veículos vinculados a ele e os próprios motoristas, custos, manutenções, modelos de contrato e integração.
          </li>
        </ul>
        {isTeam && (
          <Link to="/users" className="mt-3 inline-flex text-sm font-medium text-primary hover:underline">
            Ir para Usuários →
          </Link>
        )}
      </Panel>
      <ContractTemplatesPanel ctx={ctx} />
      <MaintenanceServicesPanel ctx={ctx} />
    </div>
  );
}

// Catálogo de serviços da manutenção (pedido 2026-10-14): já vem com os
// serviços comuns (troca de óleo, pneus, freios…) e o intervalo sugerido;
// o dono da frota ajusta, inclui ou tira.
function MaintenanceServicesPanel({ ctx }: { ctx: FleetCtx }) {
  const { data, isLoading } = useFleetMaintenanceServices(ctx.owner);
  const save = useSaveFleetMaintenanceServices(ctx.owner);
  const [items, setItems] = useState<FleetMaintenanceService[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = items ?? data?.items ?? [];
  const dirty = items !== null;
  const update = (idx: number, patch: Partial<FleetMaintenanceService>) =>
    setItems(list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const numOrNull = (v: string) => {
    const n = Number(v.replace(/\D/g, ""));
    return v.trim() && n > 0 ? n : null;
  };
  const submit = async (next: FleetMaintenanceService[] | null) => {
    if (next) {
      if (next.some((i) => !i.name.trim())) return setError("Tem serviço sem nome.");
      const names = next.map((i) => i.name.trim().toLowerCase());
      if (new Set(names).size !== names.length) return setError("Tem serviço repetido.");
    }
    setError(null);
    try {
      await save.mutateAsync(next ? next.map((i) => ({ ...i, name: i.name.trim() })) : null);
      setItems(null);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  return (
    <Panel
      title="Serviços de manutenção"
      subtitle="Aparecem prontos ao criar uma manutenção, com o intervalo da próxima já preenchido."
      className="lg:col-span-2"
      action={
        ctx.access.manage && (
          <Button size="sm" variant="outline" onClick={() => setItems([...list, { name: "", type: "preventiva", intervalKm: null, intervalDays: null }])}>
            <Plus className="h-4 w-4" /> Novo serviço
          </Button>
        )
      }
    >
      {isLoading ? (
        <ListState loading empty={false} text="" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-2">Serviço</th>
                <th className="px-2 py-2">Tipo</th>
                <th className="px-2 py-2">A cada (km)</th>
                <th className="px-2 py-2">Ou a cada (dias)</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {list.map((it, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1.5">
                    <input className={inputCls} value={it.name} disabled={!ctx.access.manage} onChange={(e) => update(idx, { name: e.target.value })} />
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      className={inputCls}
                      value={it.type}
                      disabled={!ctx.access.manage}
                      onChange={(e) => update(idx, { type: e.target.value as FleetMaintenanceService["type"] })}
                    >
                      <option value="preventiva">Preventiva</option>
                      <option value="corretiva">Corretiva</option>
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      value={it.intervalKm ?? ""}
                      disabled={!ctx.access.manage}
                      onChange={(e) => update(idx, { intervalKm: numOrNull(e.target.value) })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      value={it.intervalDays ?? ""}
                      disabled={!ctx.access.manage}
                      onChange={(e) => update(idx, { intervalDays: numOrNull(e.target.value) })}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {ctx.access.manage && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-rose-600"
                        aria-label="Remover"
                        onClick={() => setItems(list.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ctx.access.manage && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => void submit(list)}>
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Salvar serviços
          </Button>
          {dirty && (
            <Button size="sm" variant="ghost" onClick={() => setItems(null)} disabled={save.isPending}>
              Desfazer
            </Button>
          )}
          {!dirty && data && !data.isDefault && (
            <Button size="sm" variant="ghost" onClick={() => void submit(null)} disabled={save.isPending}>
              Voltar à lista padrão
            </Button>
          )}
          {error && <span className="text-sm text-rose-600">{error}</span>}
        </div>
      )}
    </Panel>
  );
}

function AsaasSettingsPanel({ ctx }: { ctx: FleetCtx }) {
  const qc = useQueryClient();
  const { data, isLoading } = useFleetSettings(ctx.owner);
  const [editing, setEditing] = useState(false);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const save = async (apiKey: string | null) => {
    setSaving(true);
    setError(null);
    try {
      await fleetManagementService.saveAsaasKey(ctx.owner, apiKey);
      qc.invalidateQueries({ queryKey: ["fleet-mgmt", "settings"] });
      qc.invalidateQueries({ queryKey: ["fleet-mgmt", "driver-billing"] });
      setEditing(false);
      setConfirmRemove(false);
      setKey("");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  const has = data?.asaas.hasApiKey;
  return (
    <Panel title="Integrações" subtitle="Conta Asaas usada para cobrar os motoristas (recorrência)">
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <span className={cn("h-2.5 w-2.5 rounded-full", has ? "bg-emerald-500" : "bg-muted-foreground/40")} />
            <span className="font-medium">Asaas</span>
            <span className="text-xs text-muted-foreground">{has ? `conectado (chave ${data?.asaas.keyHint})` : "não configurado"}</span>
          </div>
          {editing ? (
            <div className="space-y-2">
              <input
                className={inputCls}
                type="password"
                autoComplete="off"
                placeholder="Chave de API do Asaas ($aact_...)"
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">No Asaas: Integrações → Chave de API. A chave é testada antes de salvar.</p>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={() => save(key.trim())} disabled={saving || key.trim().length < 10}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Salvar chave
                </Button>
              </div>
            </div>
          ) : (
            ctx.access.manage && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant={has ? "outline" : "default"} onClick={() => { setEditing(true); setError(null); }}>
                  {has ? "Trocar chave" : "Conectar Asaas"}
                </Button>
                {has &&
                  (confirmRemove ? (
                    <>
                      <span className="self-center text-xs text-rose-600">Remover a integração?</span>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(false)}>
                        Não
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => save(null)} disabled={saving}>
                        Remover
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmRemove(true)}>
                      Remover
                    </Button>
                  ))}
              </div>
            )
          )}
          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">{error}</div>
          )}
        </div>
      )}
    </Panel>
  );
}

function ContractTemplatesPanel({ ctx }: { ctx: FleetCtx }) {
  const qc = useQueryClient();
  const { data: templates = [], isLoading } = useFleetContractTemplates(ctx.owner);
  const [editing, setEditing] = useState<FleetContractTemplate | null | undefined>(undefined);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["fleet-mgmt", "contract-templates"] });
  const open = (t: FleetContractTemplate | null) => {
    setEditing(t);
    setTitle(t?.title ?? "");
    setContent(t?.content ?? "");
    setError(null);
  };
  const save = async () => {
    if (!title.trim()) return setError("Informe o título.");
    if (!content.trim()) return setError("Escreva o texto do contrato.");
    setSaving(true);
    setError(null);
    try {
      const body = { title: title.trim(), content };
      if (editing) await fleetManagementService.updateContractTemplate(ctx.owner, editing.id, body);
      else await fleetManagementService.createContractTemplate(ctx.owner, body);
      refresh();
      setEditing(undefined);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id: string) => {
    try {
      await fleetManagementService.deleteContractTemplate(ctx.owner, id);
      setConfirmId(null);
      refresh();
    } catch (err) {
      toast.error("Não foi possível excluir", { description: errMsg(err) });
    }
  };
  return (
    <Panel
      title="Modelos de contrato"
      subtitle="Usados em Motoristas → seta → Contratos. Separados dos contratos da empresa."
      className="lg:col-span-2"
      action={<AddButton canManage={ctx.access.manage} label="Novo modelo" onClick={() => open(null)} />}
    >
      <ListState loading={isLoading} empty={!templates.length} text="Nenhum modelo criado." />
      {templates.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {templates.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{t.title}</div>
                <div className="truncate text-xs text-muted-foreground">{t.content.slice(0, 140)}</div>
              </div>
              {ctx.access.manage &&
                (confirmId === t.id ? (
                  <span className="flex items-center gap-1">
                    <span className="text-xs text-rose-600">Excluir?</span>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
                      Não
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(t.id)}>
                      Sim
                    </Button>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => open(t)} aria-label="Editar">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => setConfirmId(t.id)} aria-label="Excluir">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </span>
                ))}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={editing !== undefined} onOpenChange={(o) => !o && !saving && setEditing(undefined)}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar modelo" : "Novo modelo de contrato"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Título *">
              <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Contrato de locação para motorista de app" />
            </Field>
            <Field label="Texto do contrato *">
              <textarea className={cn(inputCls, "h-80 py-2 font-mono text-xs leading-relaxed")} value={content} onChange={(e) => setContent(e.target.value)} />
            </Field>
            <p className="text-xs text-muted-foreground">
              Marcadores preenchidos sozinhos ao gerar o contrato: [NOME], [CPF], [TELEFONE], [EMAIL], [CNH], [CATEGORIA CNH], [VEÍCULO], [PLACA],
              [VALOR], [PERIODICIDADE], [DATA], [DATA DE INÍCIO], [DATA FIM].
            </p>
            {error && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">Não foi salvo: {error}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(undefined)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
